<!--
  Nothing at all until a moderator features something: an empty "Featured" heading would only
  advertise a section the community isn't using.

  Each card has the list row's split: the tappable block is a real link (so middle-click and
  copy-link work), and the controls — the moderator menu, reactions — sit OUTSIDE it, since
  buttons can't nest inside an <a>.
-->
@if (cards.length) {
  <section class="featured-rail" aria-labelledby="featured-rail-title">
    <div class="featured-rail__header">
      <div>
        <h2 class="featured-rail__title" id="featured-rail-title">Featured</h2>
        @if (newCount) {
          <div class="featured-rail__new">
            {{ newCount }} new
            <span class="featured-rail__new-dot" aria-hidden="true"></span>
          </div>
        }
      </div>
    </div>

    <div class="featured-rail__viewport">
      @if (canScrollBack) {
        <button type="button" class="featured-rail__arrow featured-rail__arrow--back" (click)="scrollBy(-1)" aria-label="Previous featured posts">
          <fa-icon icon="chevron-left" aria-hidden="true"></fa-icon>
        </button>
      }
      <ul class="featured-rail__track" #track (scroll)="updateScrollState()">
        @for (card of cards; track card.id) {
          <li class="featured-card" [class.featured-card--unread]="card.unread">
            <div class="featured-card__top">
              <a class="featured-card__byline" [attr.href]="card.href" (click)="open(card, $event)">
                <span class="featured-card__avatar" [style.background-color]="card.authorPhoto ? 'transparent' : card.avatarColor">
                  @if (card.authorPhoto) {
                    <img [src]="card.authorPhoto" alt="" />
                  } @else {
                    <span aria-hidden="true">{{ card.initials }}</span>
                  }
                </span>
                <span class="featured-card__who">
                  <span class="featured-card__author">{{ card.authorName }}</span>
                  <span class="featured-card__date">{{ card.createdDate | date: "MMM Do [at] h:mma" : true }}</span>
                </span>
                @if (card.unread) {
                  <span class="sr-only">{{ "com.post_unread" | translate }}</span>
                }
              </a>
              @if (canManage) {
                <!-- The app's action chooser, anchored to this •••, as the post's own ••• menu is. -->
                <button type="button" class="featured-card__menu-toggle" (click)="cardOptions(card, $event.currentTarget)" aria-label="Featured post options">
                  <fa-icon icon="ellipsis-h" aria-hidden="true"></fa-icon>
                </button>
              }
            </div>

            <a class="featured-card__body" [attr.href]="card.href" (click)="open(card, $event)">
              <!-- Clamped, so a long post keeps the card its size; the post opens in full. -->
              @if (card.subject) {
                <p class="featured-card__subject">{{ card.subject }}</p>
              }
              @if (card.bodyPreview) {
                <p class="featured-card__text">{{ card.bodyPreview }}</p>
              }
              @if (card.media.kind !== "none") {
                <span class="featured-card__media" [class.featured-card__media--audio]="card.media.kind === 'audio'">
                  @if (card.media.imageUrl) {
                    <img [src]="card.media.imageUrl" alt="" loading="lazy" />
                  }
                  @if (card.media.kind === "audio" || card.media.kind === "youtube" || card.media.kind === "video") {
                    <span class="featured-card__play" aria-hidden="true"><fa-icon icon="play"></fa-icon></span>
                    <span class="sr-only">{{ card.mediaLabel }}</span>
                  }
                </span>
              }
            </a>

            <!-- Facebook's rail: a reaction summary and the comment count, then Like and Comment. -->
            @if (card.reactionTotal || card.replyCount) {
              <div class="featured-card__stats">
                <span class="featured-card__stat">
                  @if (card.reactionTotal) {
                    <span class="featured-card__emoji" aria-hidden="true">
                      @for (emoji of card.reactionEmoji; track emoji) {
                        <span>{{ emoji }}</span>
                      }
                    </span>
                    {{ card.reactionTotal }}
                    <span class="sr-only">reactions</span>
                  }
                </span>
                @if (card.replyCount) {
                  <span class="featured-card__stat">{{ card.replyCount }} {{ card.replyCount === 1 ? "comment" : "comments" }}</span>
                }
              </div>
            }
            <div class="featured-card__actions">
              <!-- Resting on Like opens every reaction, across the card — see `onLikeEnter`. -->
              @if (pickerCardId === card.id) {
                <div class="featured-card__picker" role="listbox" aria-label="Choose a reaction" (mouseenter)="onPickerEnter()" (mouseleave)="onLikeLeave()" (keydown)="onPickerKeydown(card, $event)">
                  @for (type of reactionTypes; track type; let idx = $index) {
                    <button
                      type="button"
                      class="featured-card__picker-option"
                      [id]="'featured-reaction-' + card.id + '-' + idx"
                      role="option"
                      [class.is-mine]="card.myReactionSet[type]"
                      [attr.aria-selected]="!!card.myReactionSet[type]"
                      [attr.aria-label]="reactionLabels[type]"
                      [attr.title]="reactionLabels[type]"
                      [attr.tabindex]="pickerFocus === idx ? 0 : -1"
                      (click)="pick(card, type)"
                    >
                      <span aria-hidden="true">{{ reactionEmoji[type] }}</span>
                    </button>
                  }
                </div>
              }
              <button
                type="button"
                class="featured-card__action"
                [id]="'featured-like-' + card.id"
                [class.is-on]="!!card.myReaction"
                [attr.aria-pressed]="!!card.myReaction"
                aria-haspopup="listbox"
                [attr.aria-expanded]="pickerCardId === card.id"
                (mouseenter)="onLikeEnter(card)"
                (mouseleave)="onLikeLeave()"
                (pointerdown)="onLikePointerDown(card, $event)"
                (pointerup)="onLikePointerUp()"
                (pointercancel)="onLikePointerUp()"
                (contextmenu)="$event.preventDefault()"
                (keydown)="onLikeKeydown(card, $event)"
                (click)="onLikeClick(card)"
              >
                @if (card.myReaction && card.myReaction !== "like") {
                  <span class="featured-card__my-emoji" aria-hidden="true">{{ card.myReactionEmoji }}</span>
                  {{ card.myReactionLabel }}
                } @else {
                  <fa-icon [icon]="card.myReaction ? 'thumbs-up' : ['far', 'thumbs-up']" aria-hidden="true"></fa-icon>
                  Like
                }
              </button>
              <a class="featured-card__action" [attr.href]="card.href" (click)="open(card, $event)">
                <fa-icon [icon]="['far', 'comment']" aria-hidden="true"></fa-icon>
                Comment
              </a>
              <button type="button" class="featured-card__action" [class.is-on]="copiedCardId === card.id" (click)="share(card)">
                @if (copiedCardId === card.id) {
                  <fa-icon icon="check" aria-hidden="true"></fa-icon>
                  <span aria-live="polite">{{ "com.link_copied" | translate }}</span>
                } @else {
                  <fa-icon icon="arrow-up-from-bracket" aria-hidden="true"></fa-icon>
                  {{ "com.share" | translate }}
                }
              </button>
            </div>
          </li>
        }
      </ul>
      @if (canScrollForward) {
        <button type="button" class="featured-rail__arrow featured-rail__arrow--forward" (click)="scrollBy(1)" aria-label="More featured posts">
          <fa-icon icon="chevron-right" aria-hidden="true"></fa-icon>
        </button>
      }
    </div>
  </section>
}
